// SocialLinks — Etappe 1.6 / Build 94
// Wiederverwendbare Darstellung der standardisierten Sozial-/Media-Links für
// Evenemang, Föreningar, Företag, Karttipps, Vardagskort etc.
//
// Standardfelder (Referenz-Schema für die gesamte Plattform):
//   webbplats         — generischer Web-Link (Webseite, Blog, …)
//   facebook_url      — Facebook-Page/Profil
//   instagram_url     — Instagram-Profil
//   youtube_url       — YouTube-Kanal/Video (alle Formate: watch, youtu.be, embed, …)
//   media_url         — Generischer Medien-Link (Build 89 — backwards compat)
//   media_typ         — Video / Artikel / Audio / Dokument
//   media_beskrivning — Kurze Erklärung zum Media-Link
//
// Auto-Detection: Wenn `media_url` ein YouTube-Link ist und `media_typ` fehlt,
// wird er trotzdem als Video gerendert. Damit gehen historische Daten von vor
// dem Schema-Update nicht verloren.
import { Globe, Facebook, Instagram, Youtube, Film, FileText, Headphones, Newspaper, ExternalLink } from "lucide-react";

const isHttp = (s) => typeof s === "string" && /^https?:\/\//i.test(s.trim());
const isYoutubeUrl = (s) => typeof s === "string" && /(?:youtube\.com|youtu\.be|youtube-nocookie\.com)/i.test(s);

const MEDIA_LABELS = {
  Video:    { text: "Film",     Icon: Film },
  Artikel:  { text: "Artikel",  Icon: Newspaper },
  Audio:    { text: "Audio",    Icon: Headphones },
  Dokument: { text: "Dokument", Icon: FileText },
};

function LinkPill({ href, Icon, label, testid }) {
  return (
    <a
      href={href.trim()}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-pine hover:text-pine-hover no-underline"
    >
      <Icon size={14} />
      <span>{label}</span>
      <ExternalLink size={11} className="opacity-60" />
    </a>
  );
}

export default function SocialLinks({
  webbplats,
  facebook_url,
  instagram_url,
  youtube_url,
  media_url,
  media_typ,
  media_beskrivning,
  compact = false,
}) {
  // Backwards-compat: media_url enthält Youtube-URL ohne typ → als youtube_url behandeln
  let effectiveYoutube = youtube_url;
  let effectiveMediaUrl = media_url;
  let effectiveMediaTyp = media_typ;
  if (!effectiveYoutube && isHttp(media_url) && isYoutubeUrl(media_url)) {
    effectiveYoutube = media_url;
    effectiveMediaUrl = null; // verhindert Doppel-Render
  }
  // Backwards-compat: media_url ist Youtube + media_typ=Video → konsolidiere zu youtube_url
  if (!effectiveYoutube && isHttp(media_url) && media_typ === "Video" && isYoutubeUrl(media_url)) {
    effectiveYoutube = media_url;
    effectiveMediaUrl = null;
  }

  const links = [];
  if (isHttp(webbplats))      links.push({ key: "webbplats",  href: webbplats,       Icon: Globe,     label: "Webbplats",   testid: "social-webbplats" });
  if (isHttp(facebook_url))   links.push({ key: "facebook",   href: facebook_url,    Icon: Facebook,  label: "Facebook",    testid: "social-facebook" });
  if (isHttp(instagram_url))  links.push({ key: "instagram",  href: instagram_url,   Icon: Instagram, label: "Instagram",   testid: "social-instagram" });
  if (isHttp(effectiveYoutube)) links.push({ key: "youtube",  href: effectiveYoutube, Icon: Youtube,  label: "YouTube",     testid: "social-youtube" });

  // Generischer Media-Link (Artikel/Audio/Dokument oder Video das KEIN YouTube ist)
  let mediaItem = null;
  if (isHttp(effectiveMediaUrl) && effectiveMediaTyp && MEDIA_LABELS[effectiveMediaTyp]) {
    const spec = MEDIA_LABELS[effectiveMediaTyp];
    mediaItem = { key: "media", href: effectiveMediaUrl, Icon: spec.Icon, label: spec.text, testid: `media-link-${effectiveMediaTyp.toLowerCase()}` };
  }

  if (links.length === 0 && !mediaItem) return null;

  return (
    <div data-testid="social-links" className={compact ? "flex flex-wrap gap-x-4 gap-y-2" : "mt-3 flex flex-wrap gap-x-4 gap-y-2"}>
      {links.map((l) => (
        <LinkPill key={l.key} href={l.href} Icon={l.Icon} label={l.label} testid={l.testid} />
      ))}
      {mediaItem && (
        <div className="flex flex-col">
          <LinkPill href={mediaItem.href} Icon={mediaItem.Icon} label={mediaItem.label} testid={mediaItem.testid} />
          {media_beskrivning && (
            <p className="text-xs text-bark/65 mt-1 max-w-md leading-snug">{media_beskrivning}</p>
          )}
        </div>
      )}
    </div>
  );
}
